import { UserAction, UserEvent } from '../constants/enums'
import { pubSubNotes, pubSubUsers } from '../state/pubSub'
import { safeObj } from '../utilities/safe'
import { userAction } from './workerActions'

type Promises<T> = {
    [id: number]: {
        promise?: Promise<T>
        resolve?: (result?: T) => void
        reject?: (err?: Error) => void
    }
}

const Notes: { queueTimer: ReturnType<typeof setTimeout> } = {
    queueTimer: null,
}

type SaveNoteOptions = {
    overwrite?: boolean
}
type SaveNoteCallbackProps = {
    err?: Error
    id: number
    result: string
}

const saveNoteClosure = (
    promises: Promises<string> = {},
): ((
    token: string,
    userId: number,
    note: string,
    options?: SaveNoteOptions,
) => Promise<string>) => {
    pubSubUsers.on(
        UserEvent.SAVE_NOTE,
        ({ err, id, result }: SaveNoteCallbackProps) => {
            if (err) promises[id]?.reject?.(err)
            else promises[id]?.resolve?.(result)
            delete promises[id]
        },
    )
    return (
        token: string,
        id: number,
        note: string,
        options?: SaveNoteOptions,
    ): Promise<string> => {
        if (typeof window === 'undefined') {
            return Promise.resolve(undefined)
        }
        if (!id) throw new Error(`'id' is required!`)
        if (!token) throw new Error(`'token' is required!`)
        if (!note) throw new Error(`'note' is required!`)
        if (promises[id]?.promise) {
            return promises[id].promise
        }
        promises[id] = {
            promise: undefined,
            resolve: undefined,
            reject: undefined,
        }
        promises[id].promise = new Promise((resolve, reject) => {
            promises[id].resolve = resolve
            promises[id].reject = reject
        })
        const { overwrite } = safeObj(options)
        userAction(UserAction.SAVE_NOTE, { id, note, token, overwrite })
        return promises[id].promise
    }
}

export const saveNote = saveNoteClosure()

export async function saveNoteDebounced(
    token: string,
    id: number,
    note: string,
) {
    return new Promise<string>((resolve, reject) => {
        clearTimeout(Notes.queueTimer)
        if (!id || !token) {
            reject(new Error('Missing required parameters (id and/or token).'))
            return
        }
        Notes.queueTimer = setTimeout(() => {
            saveNote(token, id, note || '', { overwrite: true })
                .then(resolve)
                .catch(reject)
        }, 1500)
    })
}

export type AddUserResult = {
    id: number
    username: string
    access_level: number
}
type AddNoteCallbackProps = {
    err?: Error
    name: string
    result: AddUserResult
}

const notesAddUserClosure = (
    promises: Promises<AddUserResult> = {},
): ((token: string, username: string) => Promise<AddUserResult>) => {
    pubSubUsers.on(
        UserEvent.ADD_NOTE,
        ({ err, name, result }: AddNoteCallbackProps) => {
            if (err) promises[name]?.reject?.(err)
            else promises[name]?.resolve?.(result)
            delete promises[name]
        },
    )
    return (token: string, username: string): Promise<AddUserResult> => {
        if (typeof window === 'undefined') {
            return Promise.resolve(undefined)
        }
        if (!username) throw new Error(`'username' is required!`)
        if (!token) throw new Error(`'token' is required!`)
        const name = username.toLowerCase()
        if (promises[name]?.promise) {
            return promises[name].promise
        }
        promises[name] = {
            promise: undefined,
            resolve: undefined,
            reject: undefined,
        }
        promises[name].promise = new Promise((resolve, reject) => {
            promises[name].resolve = resolve
            promises[name].reject = reject
        })
        userAction(UserAction.ADD_NOTE, { name, token })
        return promises[name].promise
    }
}

export const notesAddUser = notesAddUserClosure()

type DeleteNoteCallbackProps = {
    err?: Error
    id: number
}

const deleteNoteClosure = (
    promises: Promises<void> = {},
): ((token: string, id: number) => Promise<void>) => {
    pubSubUsers.on(
        UserEvent.DELETE_NOTE,
        ({ err, id }: DeleteNoteCallbackProps) => {
            if (err) promises[id]?.reject?.(err)
            else promises[id]?.resolve?.()
            delete promises[id]
        },
    )
    return (token: string, id: number): Promise<void> => {
        if (typeof window === 'undefined') {
            return Promise.resolve()
        }
        if (!id) throw new Error(`'id' is required!`)
        if (!token) throw new Error(`'token' is required!`)
        if (promises[id]?.promise) {
            return promises[id].promise
        }
        promises[id] = {
            promise: undefined,
            resolve: undefined,
            reject: undefined,
        }
        promises[id].promise = new Promise((resolve, reject) => {
            promises[id].resolve = resolve
            promises[id].reject = reject
        })
        userAction(UserAction.DELETE_NOTE, { id, token })
        return promises[id].promise
    }
}

export const deleteNote = deleteNoteClosure()

type SearchNotesResult = {
    user_id: number
    subject_id: number
    results: Array<string>
}
export type SearchNotesResults = {
    total_found: number
    /** keys are: UserID_SubjectID */
    results: Record<number, SearchNotesResult> | []
}
type SearchNotesCallbackProps = {
    err?: Error
    id: number
    result: SearchNotesResults
}

const searchNotesClosure = (
    promises: Promises<SearchNotesResults> = {},
): ((query: string) => Promise<SearchNotesResults>) => {
    pubSubUsers.on(
        UserEvent.SEARCH_NOTES,
        ({ err, id, result }: SearchNotesCallbackProps) => {
            if (err) promises[id]?.reject?.(err)
            else promises[id]?.resolve?.(result)
            delete promises[id]
        },
    )
    return (query: string): Promise<SearchNotesResults> => {
        if (typeof window === 'undefined') {
            return Promise.resolve(undefined)
        }
        const id = Math.floor(Math.random() * 100000)
        promises[id] = { resolve: undefined, promise: undefined }
        promises[id].promise = new Promise((resolve, reject) => {
            promises[id].resolve = resolve
            promises[id].reject = reject
        })
        userAction(UserAction.SEARCH_NOTES, { id, query })
        return promises[id].promise
    }
}

export const searchNotes = searchNotesClosure()

/**
 * Search notes for a given query and emit the results to the pubsub
 */
export async function notesSearch(query: string) {
    try {
        const results = await searchNotes(query)
        pubSubNotes.emit('search', results)
        return results
    } catch (err) {
        console.error(err)
        pubSubNotes.emit('search', null)
        return undefined
    }
}
